import { useEffect, useRef, useState } from 'react';

import { errors, isValid, validator } from 'react-validator';
import { useDrop } from 'react-dnd';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { TeacherGetQuestionByIdQuery } from '@school_ify/schoolify-repository/src/teacher/types';

import Input from 'components/Input/Text/Input';
import Button from 'clean/components/common/Button/Button';
import Errors from 'components/Errors/Errors';
import quizIconBlueSmall from 'assets/icons/quiz-icon-blue-small.svg';
import { ReactComponent as PlusW } from 'assets/icons/plus_w.svg';
import { CreateEditQuestionModel } from 'types/quiz';

import DraggableAnswer from './components/Answer/DraggableAnswer';

import './FillInBlankQuestion.scoped.scss';

interface FillInBlankQuestionProps {
  createOrUpdateQuestion: (question: CreateEditQuestionModel) => void;
  editQuestion?: TeacherGetQuestionByIdQuery['assignment_questions_by_pk'] | null;
}

const FillInBlankQuestion = (props: FillInBlankQuestionProps) => {
  const { createOrUpdateQuestion, editQuestion } = props;
  const dropRef = useRef(null);
  const itemsContainerRef = useRef(null);
  const [newAnswer, setNewAnswer] = useState('');
  const [answers, setAnswers] = useState([]);
  const [newAnsErr, setNewAnsErr] = useState(false);
  const [validation, setValidation] = useState({});
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    if (editQuestion?.id) {
      setAnswers(editQuestion.assignment_question_options);
    }
  }, [editQuestion]);

  const onCreate = () => {
    if (dropRef?.current) {
      dropRef.current.innerHTML = dropRef.current.innerHTML.replace('<br>', '');
      const editedContent = dropRef.current.innerHTML.replace(/(<span.*?span>)/g, '<answer>');
      const model = { title: editedContent };
      validator(
        { model },
        {
          'model.title': ['required', 'string'],
        },
        {
          'model.title.required': t('study:title_required'),
        }
      );
      setValidation(errors());
      if (!isValid()) {
        return;
      }

      const orderedOptions = Array.from(dropRef.current.children).map(
        (child: HTMLElement, index) => {
          const isISODate = DateTime.fromISO(child.id);
          return {
            is_correct: true,
            title: child.innerText,
            order: index,
            ...(isISODate.invalidReason && { id: child.id }),
          };
        }
      );
      createOrUpdateQuestion({
        title: editedContent,
        content: '',
        questionOptions: orderedOptions,
      });
    }
  };

  const handleAddAnswer = () => {
    if (newAnswer) {
      setAnswers([...answers, { id: DateTime.now().toISO(), title: newAnswer }]);
      setNewAnswer('');
      setNewAnsErr(false);
    } else {
      setNewAnsErr(true);
    }
  };

  const insertAnswer = (dragItem, monitor) => {
    const offset = monitor.getClientOffset();
    let range = null;

    if (document.caretRangeFromPoint) {
      // Chrome
      range = document.caretRangeFromPoint(offset.x, offset.y);
      // @ts-ignore
    } else if (document?.caretPositionFromPoint) {
      // Firefox
      range = document.createRange();
      // @ts-ignore
      const caretPosition = document.caretPositionFromPoint(offset.x, offset.y);
      range.setStart(caretPosition.offsetNode, caretPosition.offset);
    }

    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);

    range.insertNode(dragItem.current);

    sel.removeAllRanges();
  };

  const [, drop] = useDrop(() => ({
    accept: 'answer',
    drop: (item, monitor) => insertAnswer(item, monitor),
  }));

  drop(dropRef);

  const removeAnswer = (id: string) => {
    setAnswers(answers.filter(answer => answer.id !== id));
  };

  const onComponentRender = () => {
    if (
      editQuestion?.title &&
      editQuestion?.assignment_question_options?.length ===
        itemsContainerRef.current.children.length &&
      dropRef.current.innerHTML === ''
    ) {
      dropRef.current.focus();

      let questionText = editQuestion.title.replaceAll('&nbsp;', ' ');
      const slicedQuestionText = [];
      while (questionText.indexOf('<answer>') !== -1) {
        const indexOfAnswer = questionText.indexOf('<answer>');
        slicedQuestionText.push(questionText.slice(0, indexOfAnswer));
        slicedQuestionText.push('<answer>');
        questionText = questionText.slice(indexOfAnswer + 8);
      }
      slicedQuestionText.push(questionText);
      const answerElements = Array.from(itemsContainerRef.current.children);

      let answerCounter = 0;
      slicedQuestionText.forEach(element => {
        if (element === '<answer>') {
          dropRef.current.appendChild(answerElements[answerCounter]);
          answerCounter += 1;
        } else {
          const textNode = document.createTextNode(element);
          dropRef.current.appendChild(textNode);
        }
      });
    }
  };

  const handleAddAnswerOnEnter = event => {
    if (event.keyCode === 13) {
      handleAddAnswer();
    }
  };

  return (
    <div className="quizquestion-container">
      <div className="create-quiz-center">
        <div className="title-container">
          <h2 className="new-quiz-title">
            <img alt="Quiz icon blue" src={quizIconBlueSmall} width="21" height="21" />
            {t('quiz:Question.FillBlankForm')}
          </h2>
        </div>

        <div className="quiz-components-wrapper">
          <label className="form-label label-required">
            {t('quiz:Question.Description')}
            <span>*</span>
          </label>
          <div
            className="answer-textarea"
            role="textbox"
            ref={dropRef}
            contentEditable="true"
            suppressContentEditableWarning={true}
          />
          <Errors errors={validation} prop={'model.title'} />
          <label className="form-label">{t('quiz:Question.NewAnswer')}</label>
          <div className="quiz-new-answer">
            <div>
              <Input
                onChange={value => setNewAnswer(value)}
                type="text"
                style={{
                  paddingBottom: '0px',
                  paddingTop: '0px',
                  marginBottom: '20px',
                  height: '37px',
                  width: '300px',
                }}
                onKeyDown={event => handleAddAnswerOnEnter(event)}
                placeholder={t('quiz:Question.TypeAnswer')}
                value={newAnswer}
              />
              {newAnsErr && (
                <p
                  className="answer-error"
                  style={{
                    color: 'red',
                  }}
                >
                  {t('quiz:Question.TypeAnswer')}
                </p>
              )}
            </div>
            <div className="plus" onClick={() => handleAddAnswer()}>
              <PlusW />
            </div>
          </div>
        </div>

        <div className="your-answers-container">
          <h4>{t('quiz:Question.YourAnswers')}</h4>
          <div className="all-answers" ref={itemsContainerRef}>
            {answers.map(answer => (
              <DraggableAnswer
                answer={answer}
                key={answer.id}
                id={answer.id}
                containerRef={itemsContainerRef}
                removeAnswer={removeAnswer}
                onComponentRender={onComponentRender}
              />
            ))}
          </div>
          {answers.length > 0 && <p>{t('quiz:Question.DragAnswer')}</p>}

          {answers.length === 0 && <p>{t('quiz:Question.AddAnswers')}</p>}
        </div>
      </div>
      <div className="button-wrapper">
        <Button
          onClick={() => onCreate()}
          label={t('quiz:Question.Create')}
          variant="filled"
          color="primary"
        />
        <Button onClick={() => history.goBack()} label={t('common:cancel')} color="decline" />
      </div>
    </div>
  );
};

export default FillInBlankQuestion;
